import { Link } from "react-router-dom";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import {
  Frame,
  FrameFooter,
  FrameHeader,
  FrameTitle,
} from "@volca/ui/components/ui/frame";
import { Table, TableBody } from "@volca/ui/components/ui/table";
import { Text } from "@volca/ui/components/ui/text";
import { count } from "@volca/ui/trades";
import { useReportCurrency } from "@/profitability/currency";
import {
  type RecordObject,
  type RecordRef,
  sameRecord,
} from "@/profitability/records/objects";
import type { RecordList } from "@/profitability/records/record-panel";
import { FindingRow, findingRef } from "./finding-row";
import { GroupActions } from "./group-actions";
import { causeName, money, nextStepName } from "./labels";
import { isProfit, type ReviewSpec, useReviewSpec } from "./review-spec";
import type { Finding } from "./types";

/**
 * The findings a group a rule, in the owner's order when they set one
 * (`spec.rule_order`): a group goes where its first rule is placed, and one
 * with no placed rule (a flag written as text alone) after them. Otherwise,
 * and among the unplaced, what each costs first, then how many it holds.
 */
export const byRule = (findings: Finding[], order: string[] = []) => {
  const groups = new Map<string, Finding[]>();
  for (const finding of findings) {
    // The API's rule (`finding.rule`), never the flag split again here.
    const rule = finding.rule;
    groups.set(rule, [...(groups.get(rule) ?? []), finding]);
  }
  const below = (items: Finding[]) =>
    items.reduce((sum, item) => sum + Math.max(item.shortfall ?? 0, 0), 0);
  const place = (items: Finding[]) => {
    const index = order.indexOf(items[0]?.rule_ids?.[0] ?? "");
    return index === -1 ? order.length : index;
  };
  return [...groups.entries()]
    .map(([rule, items]) => ({
      rule,
      items,
      below: below(items),
      place: place(items),
    }))
    .sort(
      (a, b) =>
        a.place - b.place ||
        b.below - a.below ||
        b.items.length - a.items.length,
    );
};

/**
 * What Volca makes of a group: the cause it guessed most often, on how many,
 * and the next step it suggests for those. Null when it guessed nothing.
 */
const groupRead = (items: Finding[], spec: ReviewSpec) => {
  const tally = new Map<string, Finding[]>();
  for (const item of items) {
    const cause = item.hypothesis?.cause;
    if (cause) tally.set(cause, [...(tally.get(cause) ?? []), item]);
  }
  const [cause, sharing] =
    [...tally.entries()].sort((a, b) => b[1].length - a[1].length)[0] ?? [];
  if (!cause || !sharing) return null;
  const first = sharing[0]?.hypothesis;
  return {
    cause,
    name: causeName(cause, first?.cause_name, spec.causes),
    share: sharing.length,
    action: first?.action
      ? nextStepName(first.action, first.action_label, spec.next_steps)
      : (first?.action_label ?? null),
  };
};

/**
 * The findings to review, a frame per rule that flagged them, as the
 * records tabs are: the rule, its count, what it costs and its menu in the
 * frame's header, its records as rows of a card table, and Volca's read of
 * the group in the footer when it has one. A reviewer clears a rule at a
 * time, so the rule's menu acts on all of them (and asks Volca about them).
 */
export const ReviewList = ({
  businessId,
  findings,
  objects,
  selected,
  onOpenRecord,
  onAsk,
  canUpload = false,
  totals,
  viewAll,
}: {
  businessId: string;
  findings: Finding[];
  objects: RecordObject[];
  selected: RecordRef | null;
  onOpenRecord: (ref: RecordRef, list?: RecordList | null) => void;
  onAsk?: (question: string) => void;
  /** Whether a finding missing figures offers the upload that fills them. */
  canUpload?: boolean;
  /** How many findings the rule has, when the rows are only a preview. */
  totals?: Record<string, number>;
  /** Review, filtered to this rule. Shown when the preview leaves some out. */
  viewAll?: (rule: string) => string;
}) => {
  const spec = useReviewSpec();
  const currency = useReportCurrency();
  const groups = byRule(findings, spec.rule_order);
  const list: RecordList = {
    label: "Review",
    refs: groups.flatMap((group) => group.items.map(findingRef)),
  };
  return (
    <div className="@container flex flex-col gap-4">
      {groups.map((group) => {
        const object = objects.find(
          (item) => item.dataset.id === group.items[0]?.dataset,
        );
        const noun = (object?.name ?? "record").toLowerCase();
        const plural = (object?.plural ?? `${noun}s`).toLowerCase();
        const read = groupRead(group.items, spec);
        const total = totals?.[group.rule] ?? group.items.length;
        const preview = total > group.items.length;
        const viewAllHref = preview ? viewAll?.(group.rule) : undefined;
        return (
          <section key={group.rule} aria-label={group.rule}>
            <Frame spacing="xs">
              <FrameHeader className="flex-row flex-wrap items-center gap-x-2 gap-y-1">
                <FrameTitle
                  role="heading"
                  aria-level={4}
                  className="min-w-0 text-pretty"
                >
                  {group.rule}
                </FrameTitle>
                <Badge
                  variant="secondary"
                  size="sm"
                  className="shrink-0 tabular-nums"
                >
                  {count(total, noun, plural)}
                </Badge>
                <span className="ms-auto flex shrink-0 items-center gap-2">
                  {viewAllHref && (
                    <Button
                      variant="ghost"
                      size="sm"
                      nativeButton={false}
                      render={<Link to={viewAllHref} />}
                    >
                      View all
                    </Button>
                  )}
                  {/* Quiet beside the rule: only the amount carries the colour. */}
                  {!preview && isProfit(spec) && group.below > 0 && (
                    <Text
                      variant="caption"
                      tabular
                      className="text-subtle"
                      title={`What these ${plural} came in under the average ${spec.metric.label}`}
                    >
                      <span className="text-ink">
                        {money(group.below, currency)}
                      </span>{" "}
                      under average
                    </Text>
                  )}
                  <GroupActions
                    businessId={businessId}
                    rule={group.rule}
                    findings={group.items}
                    noun={noun}
                    plural={plural}
                    bulk={!preview}
                    onAsk={
                      onAsk &&
                      (() =>
                        onAsk(
                          `Why were ${count(total, noun, plural)} flagged for “${group.rule}”, and which should I check first?`,
                        ))
                    }
                  />
                </span>
              </FrameHeader>
              <Table variant="card" aria-label={`${group.rule}: ${plural}`}>
                <TableBody>
                  {group.items.map((finding) => {
                    const ref = findingRef(finding);
                    return (
                      <FindingRow
                        key={finding.id}
                        businessId={businessId}
                        finding={finding}
                        object={object}
                        objects={objects}
                        selected={sameRecord(selected, ref)}
                        cause={read?.cause ?? null}
                        canUpload={canUpload}
                        showPeriod={
                          new Set(group.items.map((item) => item.start_date))
                            .size > 1
                        }
                        onOpen={() => onOpenRecord(ref, list)}
                        onOpenRecord={(target) => onOpenRecord(target, null)}
                      />
                    );
                  })}
                </TableBody>
              </Table>
              {read && !preview && (
                <FrameFooter className="flex-row flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                  <span className="flex items-center gap-2">
                    <span className="text-subtle">Likely cause</span>
                    <span className="text-ink">
                      {read.name}
                      {read.share < group.items.length && (
                        <span className="text-subtle">
                          {" "}
                          ({read.share} of {group.items.length})
                        </span>
                      )}
                    </span>
                  </span>
                  {read.action && (
                    <span className="flex items-center gap-2">
                      <span className="text-subtle">Next step</span>
                      <span className="text-ink">{read.action}</span>
                    </span>
                  )}
                </FrameFooter>
              )}
            </Frame>
          </section>
        );
      })}
    </div>
  );
};
